/**
 * Global email composer (SPEC-2 comms): any screen opens it with a recipient
 * list via useComposer().openEmail([...]). Renders as an overlay so the flow is
 * identical from the customer card, the list multi-select and promo matches.
 *
 * Flow: recipients → template (Company / My store) → edit (lite rich text,
 * merge tokens, attachments ≤3MB) → send → per-recipient result report.
 * All guards (can_email, do-not-contact, 30-day collision) are server-side;
 * this UI just renders their outcome honestly.
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { useApi } from '@/hooks/useApi';
import { useUserRole } from '@/hooks/useUserRole';
import type { CommTemplate, Attachment, SendResult } from '@/api/comms';
import '@/styles/composer.css';

export interface Recipient {
  customer_id: string;
  name: string;
  /** null = known missing (warn); undefined = unknown here, resolved server-side. */
  email?: string | null;
}

interface ComposerApi {
  openEmail: (recipients: Recipient[]) => void;
}

const ComposerContext = createContext<ComposerApi | null>(null);

export function useComposer(): ComposerApi {
  const ctx = useContext(ComposerContext);
  if (!ctx) throw new Error('useComposer outside ComposerProvider');
  return ctx;
}

const MAX_ATTACH = 3 * 1024 * 1024;

export function ComposerProvider({ children }: { children: ReactNode }) {
  const [recipients, setRecipients] = useState<Recipient[] | null>(null);
  const openEmail = useCallback((r: Recipient[]) => setRecipients(r), []);
  return (
    <ComposerContext.Provider value={{ openEmail }}>
      {children}
      {recipients && <ComposerModal recipients={recipients} onClose={() => setRecipients(null)} />}
    </ComposerContext.Provider>
  );
}

function ComposerModal({ recipients, onClose }: { recipients: Recipient[]; onClose: () => void }) {
  const api = useApi();
  const { seesAll } = useUserRole();
  const [templates, setTemplates] = useState<CommTemplate[]>([]);
  const [templateId, setTemplateId] = useState('');
  const [subject, setSubject] = useState('');
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<SendResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    api.commTemplates().then(setTemplates).catch(() => {});
  }, [api]);

  const applyTemplate = (id: string) => {
    setTemplateId(id);
    const t = templates.find((x) => x.template_id === id);
    if (t) {
      setSubject(t.subject);
      if (bodyRef.current) bodyRef.current.innerHTML = t.body_html;
    }
  };

  const exec = (cmd: string) => {
    document.execCommand(cmd);
    bodyRef.current?.focus();
  };

  async function addFiles(files: FileList | null) {
    if (!files) return;
    const next: Attachment[] = [...attachments];
    let total = next.reduce((n, a) => n + Math.ceil((a.contentBase64.length * 3) / 4), 0);
    for (const f of Array.from(files)) {
      if (total + f.size > MAX_ATTACH) {
        setError(`"${f.name}" would push attachments over 3MB — link large brochures instead.`);
        continue;
      }
      total += f.size;
      const buf = await f.arrayBuffer();
      let bin = '';
      const bytes = new Uint8Array(buf);
      for (let i = 0; i < bytes.length; i += 0x8000) {
        bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
      }
      next.push({ fileName: f.name, contentType: f.type || 'application/octet-stream', contentBase64: btoa(bin) });
    }
    setAttachments(next);
  }

  async function saveAsTemplate(scope: 'company' | 'store') {
    const name = window.prompt(`Template name (${scope === 'company' ? 'Company' : 'My store'}):`);
    if (!name?.trim()) return;
    try {
      setTemplates(await api.saveCommTemplate({
        name: name.trim(), subject, bodyHtml: bodyRef.current?.innerHTML ?? '', scope, attachments,
      }));
    } catch { setError('Couldn’t save the template.'); }
  }

  async function send() {
    const bodyHtml = bodyRef.current?.innerHTML ?? '';
    if (!subject.trim() || !bodyHtml.trim()) { setError('Subject and message are required.'); return; }
    setSending(true); setError(null);
    try {
      setResult(await api.sendEmail({
        customerIds: recipients.map((r) => r.customer_id),
        subject, bodyHtml,
        templateId: templateId || undefined,
        attachments: attachments.length ? attachments : undefined,
      }));
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(msg ?? 'Send failed.');
    } finally { setSending(false); }
  }

  const company = templates.filter((t) => t.scope === 'company');
  const mine = templates.filter((t) => t.scope === 'store');
  const noEmail = recipients.filter((r) => r.email === null).length;

  const REASONS: Record<string, string> = {
    no_email: 'no email address',
    do_not_contact: 'opted out of contact',
    contacted_recently: 'emailed within the last 30 days',
    not_found: 'not found',
  };

  return (
    <div className="composer-backdrop" role="dialog" aria-modal="true" aria-label="Send email">
      <div className="composer">
        {result ? (
          <div className="composer-result">
            <p className="section-title">Send report</p>
            <p className="composer-result-line composer-result-line--ok">
              ✓ Sent to {result.sent.length} customer{result.sent.length === 1 ? '' : 's'}
              {result.sent.length > 0 && `: ${result.sent.map((s) => s.name).join(', ')}`}
            </p>
            {result.skipped.length > 0 && (
              <div className="composer-result-line composer-result-line--skip">
                Skipped {result.skipped.length}:
                <ul>{result.skipped.map((s) => <li key={s.customer_id}>{s.name} — {REASONS[s.reason]}</li>)}</ul>
              </div>
            )}
            {result.failed.length > 0 && (
              <div className="composer-result-line composer-result-line--fail">
                Failed {result.failed.length}:
                <ul>{result.failed.map((s) => <li key={s.customer_id}>{s.name} — {s.error}</li>)}</ul>
              </div>
            )}
            <div className="detail-actions"><button className="btn" onClick={onClose}>Done</button></div>
          </div>
        ) : (
          <>
            <div className="composer-head">
              <p className="composer-title">Email {recipients.length} customer{recipients.length === 1 ? '' : 's'}</p>
              <button className="btn btn--ghost btn--sm" onClick={onClose} disabled={sending}>Cancel</button>
            </div>

            <div className="composer-to">
              {recipients.map((r) => (
                <span key={r.customer_id} className={`chip${r.email === null ? ' chip--muted' : ''}`}>
                  {r.name}{r.email === null && ' (no email)'}
                </span>
              ))}
            </div>
            {noEmail > 0 && (
              <p className="composer-warn">{noEmail} recipient{noEmail === 1 ? ' has' : 's have'} no email and will be skipped.</p>
            )}

            <div className="composer-row">
              <select value={templateId} onChange={(e) => applyTemplate(e.target.value)} aria-label="Template">
                <option value="">Start from a template…</option>
                {company.length > 0 && (
                  <optgroup label="Company">
                    {company.map((t) => <option key={t.template_id} value={t.template_id}>{t.name}</option>)}
                  </optgroup>
                )}
                {mine.length > 0 && (
                  <optgroup label={seesAll ? 'Store templates' : 'My store'}>
                    {mine.map((t) => <option key={t.template_id} value={t.template_id}>{t.name}</option>)}
                  </optgroup>
                )}
              </select>
              {seesAll && templateId && templates.find((t) => t.template_id === templateId)?.scope === 'store' && (
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  title="Clone this store template into the Company group (attachments included)"
                  onClick={() => {
                    api.promoteCommTemplate(templateId)
                      .then(setTemplates)
                      .catch(() => setError('Couldn’t promote the template.'));
                  }}
                >
                  → Company
                </button>
              )}
              <span className="composer-hint">Tokens: {'{{first_name}}'} {'{{last_name}}'} {'{{store}}'}</span>
            </div>

            <input
              className="composer-subject"
              placeholder="Subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />

            <div className="composer-toolbar">
              <button type="button" onClick={() => exec('bold')} title="Bold"><b>B</b></button>
              <button type="button" onClick={() => exec('italic')} title="Italic"><i>I</i></button>
              <button type="button" onClick={() => exec('underline')} title="Underline"><u>U</u></button>
              <button type="button" onClick={() => exec('insertUnorderedList')} title="Bullet list">• list</button>
            </div>
            <div ref={bodyRef} className="composer-body" contentEditable suppressContentEditableWarning aria-label="Message body" />

            <div className="composer-attach">
              <label className="btn btn--ghost btn--sm">
                Attach file…
                <input type="file" multiple hidden onChange={(e) => { void addFiles(e.target.files); e.target.value = ''; }} />
              </label>
              {attachments.map((a, i) => (
                <span key={i} className="chip chip--fav">
                  {a.fileName}
                  <button type="button" className="chip-x" onClick={() => setAttachments(attachments.filter((_, j) => j !== i))}>×</button>
                </span>
              ))}
            </div>

            {error && <p className="state-msg state-msg--error">{error}</p>}

            <div className="composer-actions">
              <div>
                <button className="btn btn--ghost btn--sm" onClick={() => saveAsTemplate('store')} disabled={sending}>
                  Save as my template
                </button>
                {seesAll && (
                  <button className="btn btn--ghost btn--sm" onClick={() => saveAsTemplate('company')} disabled={sending}>
                    Save as company template
                  </button>
                )}
              </div>
              <button className="btn" onClick={send} disabled={sending || recipients.length === noEmail}>
                {sending ? 'Sending…' : `Send${noEmail ? ` to ${recipients.length - noEmail}` : recipients.length > 1 ? ` to ${recipients.length}` : ''}`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
